// «Кабинеты» — только для владельца сервера (Ещё → Кабинеты): какие кабинеты есть и кто в них входит, новый кабинет,
// перенос из первой версии (файл с листами её таблицы), пригласить по адресу Google, убрать доступ.
// Данных кабинетов здесь нет — только названия, количества и адреса людей.
import { plural, ruDateShort } from '@praktika/core';
import { call, rpc } from '../lib/api.ts';
import { $, $$, busy, esc, toast } from '../lib/dom.ts';
import { SCREENS, render, title } from '../lib/nav.ts';
import { chooseSheet, closeSheet, openSheet, opt, promptSheet } from '../lib/sheet.ts';
import { bindCommon } from './common.ts';

interface Person { email: string; name: string; role: 'owner' | 'assistant'; joined: boolean; lastLogin: string }
interface Practice { id: string; name: string; isDemo: boolean; created: string; clients: number; sessions: number;
  people: Person[]; calendar: string; calendarIsSource: boolean; recent: number }

let list: Practice[] | null = null, loading = false;

const roleLabel = (r: string) => (r === 'assistant' ? 'помощник' : 'владелец');
const ROLES = [{ value: 'owner', label: 'Владелец', hint: 'ведёт кабинет' },
  { value: 'assistant', label: 'Помощник', hint: 'тоже видит и ведёт кабинет' }];

function refresh(): void { list = null; render(); }

async function load(): Promise<void> {
  if (loading) return;
  loading = true;
  try { list = await rpc<Practice[]>('adminPractices'); } catch (e) { toast((e as Error).message, true); list = []; }
  finally { loading = false; }
  render();
}

function personLine(p: Person): string {
  const when = p.joined ? (p.lastLogin ? 'входил ' + ruDateShort(p.lastLogin.slice(0, 10)) : 'в кабинете') :
    'приглашён, ещё не входил';
  return '<span class="k">' + esc(p.email) + (p.name ? '<br><span style="color:var(--muted);font-weight:400;font-size:12.5px">' +
    esc(p.name) + '</span>' : '') + '</span><span class="v chev small">' + esc(roleLabel(p.role) + ' · ' + when) + '</span>';
}

SCREENS.practices = (app) => {
  if (!list) {
    app.innerHTML = title('Кабинеты') + '<div class="empty">Загружаю…</div>';
    void load();
    return;
  }
  app.innerHTML = title('Кабинеты', list.length + '') +
    '<div class="p-sum">Кабинеты на этом сервере и кто в них входит. Сами данные кабинетов здесь не видны.</div>' +
    '<div class="btnrow"><button class="btn sec" data-new>Новый кабинет</button>' +
      '<button class="btn sec" data-import>Перенести из первой версии</button></div>' +
    '<input type="file" accept=".json,application/json" data-file hidden>' +
    list.map((p) => '<div class="card"><div class="hd">' + esc(p.name) + (p.isDemo && !/проверочн/i.test(p.name) ?
      ' · проверочный' : '') + '</div>' +
      '<div class="kv"><span class="k">Клиенты и сессии</span><span class="v">' + p.clients + ' · ' + p.sessions + '</span></div>' +
      '<div class="kv"><span class="k">Календарь</span><span class="v small">' + esc(p.calendar || 'не подключён') +
        (p.calendarIsSource ? '' : ' · не тот, из которого перенесены сессии') + '</span></div>' +
      (p.recent ? '<button class="kv act" data-dedupe="' + esc(p.id) + '"><span class="k">Убрать повторы сессий</span>' +
        '<span class="v chev small">заведено после переноса: ' + p.recent + '</span></button>' : '') +
      p.people.map((x) => '<button class="kv act" data-person="' + esc(p.id) + '" data-email="' + esc(x.email) + '">' +
        personLine(x) + '</button>').join('') +
      '<button class="kv act" data-invite="' + esc(p.id) + '"><span class="k">+ Пригласить по адресу Google</span>' +
        '<span class="v chev"></span></button></div>').join('') +
    '<p class="p-note">Человек входит через Google тем адресом, который здесь вписан, и сразу попадает в свой кабинет.</p>';
  bindCommon(app);

  $('[data-new]', app).onclick = () => promptSheet('Новый кабинет: название', '', 'text', (name) => {
    if (!name.trim()) return;
    promptSheet('Адрес Google владельца', '', 'email', (email) => {
      void call('adminCreatePractice', { name, ownerEmail: email }).then(() => { toast('Кабинет создан'); refresh(); }, () => {});
    }, 'Этим адресом владелец войдёт в приложение.');
  });

  const file = $<HTMLInputElement>('[data-file]', app);
  $('[data-import]', app).onclick = () => { file.value = ''; file.click(); };
  file.onchange = () => {
    const f = file.files?.[0];
    if (!f) return;
    busy(true);
    f.text().then((t) => {
      busy(false);
      let dump: unknown;
      try { dump = JSON.parse(t); } catch { toast('Это не файл с листами таблицы', true); return; }
      importSteps(dump);
    }, () => { busy(false); toast('Файл не прочитался', true); });
  };

  $$('[data-invite]', app).forEach((b) => { b.onclick = () => {
    const pid = b.getAttribute('data-invite')!;
    promptSheet('Пригласить по адресу Google', '', 'email', (email) => {
      chooseSheet('Кем', ROLES, 'owner', (role) => {
        void call('adminInvite', { practiceId: pid, email, role }).then(() => { toast('Приглашён'); refresh(); }, () => {});
      });
    }, 'Человек войдёт в приложение этим адресом и сразу окажется в кабинете.');
  }; });

  // повторы: сначала подсчёт, потом — после подтверждения — уборка
  $$('[data-dedupe]', app).forEach((b) => { b.onclick = () => {
    const pid = b.getAttribute('data-dedupe')!;
    void call<{ sessions: number; moved: number; clients: number }>('adminDedupe', { practiceId: pid }).then((r) => {
      if (!r.sessions && !r.moved && !r.clients) { toast('Повторов нет'); return; }
      const sh = openSheet('<div class="ttl">Убрать повторы</div><p class="sub">Сессий-повторов: ' + r.sessions +
        ' (та же встреча записана второй раз); перенесённых в календаре позже — старая дата уберётся: ' + r.moved +
        '; клиентов-повторов: ' + r.clients + ' (их сессии перейдут к тому же клиенту). Встречи в календаре не ' +
        'трогаются.</p>' + opt('warn', '', 'Убрать'));
      $('button.opt', sh).onclick = () => {
        closeSheet();
        void call<{ sessions: number; moved: number; clients: number }>('adminDedupe', { practiceId: pid, apply: true })
          .then((x) => { toast('Убрано: повторов ' + x.sessions + ', старых дат ' + x.moved + ', клиентов ' + x.clients);
            refresh(); }, () => {});
      };
    }, () => {});
  }; });

  $$('[data-person]', app).forEach((b) => { b.onclick = () => {
    const pid = b.getAttribute('data-person')!, email = b.getAttribute('data-email')!;
    const pr = list!.find((x) => x.id === pid);
    const sh = openSheet('<div class="ttl">' + esc(email) + '</div><p class="sub">Кабинет «' + esc(pr?.name ?? '') +
      '». После этого человек больше не сможет в него войти.</p>' + opt('warn', '', 'Убрать доступ'));
    $('button.opt', sh).onclick = () => {
      closeSheet();
      void call('adminRemoveAccess', { practiceId: pid, email }).then(() => { toast('Доступ убран'); refresh(); }, () => {});
    };
  }; });
};

// Перенос: название → владелец → прошедшие без статуса → календарь → перенести.
function importSteps(dump: unknown): void {
  promptSheet('Название кабинета', 'Практика', 'text', (name) => {
    if (!name.trim()) return;
    promptSheet('Адрес Google владельца', '', 'email', (email) => {
      chooseSheet('Прошедшие сессии без статуса', [
        { value: true, label: 'Отметить «Прошла»', hint: 'чтобы не висели в «Ждут статуса»' },
        { value: false, label: 'Оставить как есть', hint: 'отметить потом в приложении' }], true, (pastDone) => {
        chooseSheet('Календарь Google в новом кабинете', [
          { value: true, label: 'Только читать', hint: 'пока работает первая версия — без дублей встреч' },
          { value: false, label: 'Как в первой версии', hint: 'когда первой версией больше не пользуются' }], true,
        (readOnlyCalendar) => {
          void call<{ clients: number; sessions: number; payments: number; pastDone: number; unknownCols: string[] }>(
            'adminImportV1', { name, ownerEmail: email, dump, pastDone, readOnlyCalendar }).then((r) => {
            toast('Перенесено: ' + r.clients + ' ' + plural(r.clients, 'клиент', 'клиента', 'клиентов') + ', ' + r.sessions +
              ' ' + plural(r.sessions, 'сессия', 'сессии', 'сессий') + (r.pastDone ? ', «Прошла» — ' + r.pastDone : '') +
              (r.unknownCols.length ? '; незнакомые колонки: ' + r.unknownCols.length : ''));
            refresh();
          }, () => {});
        });
      });
    }, 'Этим адресом владелец войдёт в приложение.');
  });
}
